import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { userRoleService } from '../../../services/userRoleService';

const RoleAssignmentModal = ({ supplier, roles, onClose, onSuccess }) => {
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableRoles = roles?.filter(role => 
    role?.roleType === 'production_supplier' || 
    role?.roleType === 'dentist_supplier' ||
    role?.roleType === 'dentist_client'
  );

  const handleAssignRole = async () => {
    if (!selectedRoleId) {
      setError('Veuillez sélectionner un rôle');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await userRoleService?.assignRole({
        userId: supplier?.id,
        roleId: selectedRoleId,
        isActive: true
      });

      onSuccess();
    } catch (err) {
      console.error('Error assigning role:', err);
      setError('Erreur lors de l\'affectation du rôle');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (assignmentId) => {
    try {
      setLoading(true);
      await userRoleService?.removeRoleAssignment(assignmentId);
      onSuccess();
    } catch (err) {
      console.error('Error removing role:', err);
      setError('Erreur lors de la suppression du rôle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestion des Rôles</h2>
            <p className="text-sm text-gray-600 mt-1">
              {supplier?.companyName || supplier?.fullName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Roles */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Rôles Actuels</h3>
            {supplier?.roleAssignments?.length > 0 ? (
              <div className="space-y-2">
                {supplier?.roleAssignments?.map(assignment => (
                  <div
                    key={assignment?.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Icon name="Shield" size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{assignment?.role?.roleName}</p>
                        <p className="text-xs text-gray-500">{assignment?.role?.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveRole(assignment?.id)}
                      disabled={loading}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Aucun rôle assigné</p>
            )}
          </div>

          {/* Add New Role */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Ajouter un Rôle</h3>
            <div className="space-y-4">
              <Select
                label="Sélectionner un rôle"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e?.target?.value)}
                options={[
                  { value: '', label: 'Choisir un rôle...' },
                  ...availableRoles?.map(role => ({
                    value: role?.id,
                    label: `${role?.roleName} ${role?.canBeClient && role?.canBeSupplier ? '(Client & Fournisseur)' : role?.canBeSupplier ? '(Fournisseur)' : '(Client)'}`
                  }))
                ]}
              />

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Rôles Doubles</p>
                    <p>Un dentiste peut être simultanément client et fournisseur. Un fournisseur de production ne peut être que fournisseur.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleAssignRole} disabled={loading || !selectedRoleId}>
            {loading ? 'Affectation...' : 'Affecter le Rôle'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;